import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface CampaignMetrics {
  campaign: {
    id: string;
    name: string;
    category: string;
    regions: string[] | null;
    surfaces: string[] | null;
    active: boolean;
    displayMode: 'catalog' | 'storefront' | null;
    createdAt: string;
  };
  budget: {
    total: number;
    used: number;
    remaining: number;
    daysRemaining: number;
  };
  impressionsByDay: { date: string; count: number }[];
  bySurface: {
    mechanic_os: number;
    b2c_quote: number;
    b2c_app: number;
  };
  topVehicles: { make: string; model: string; impressions: number }[];
  totalImpressions: number;
  totalClicks: number;
  ctr: number;
  influencedQuotes: number;
}

export function useCampaignDetail(campaignId: string) {
  return useQuery<CampaignMetrics>({
    queryKey: ['campaign-detail', campaignId],
    queryFn: async () => {
      const res = await api.get(`/partners/my/campaigns/${campaignId}/metrics`);
      return res.data;
    },
    enabled: !!campaignId,
  });
}

export function usePauseCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (campaignId: string) => {
      const res = await api.post(`/partners/my/campaigns/${campaignId}/pause`);
      return res.data;
    },
    onSuccess: (_data, campaignId) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-detail', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['partner-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['partner-dashboard'] });
    },
  });
}

export function useResumeCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (campaignId: string) => {
      const res = await api.post(`/partners/my/campaigns/${campaignId}/resume`);
      return res.data;
    },
    onSuccess: (_data, campaignId) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-detail', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['partner-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['partner-dashboard'] });
    },
  });
}
